"use client"

import { FormEvent, KeyboardEvent, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Brain, Loader2Icon, Paperclip, Send } from "lucide-react"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { FilePreview } from "./FilePreview"
import { getAcceptString, isAllowedFile } from "@/services/uploadService"
import { useRole } from "@/hooks/useRole"

interface ChatInputProps {
  input: string
  setInput: (input: string) => void
  handleSubmit: (e: FormEvent) => void
  isLoading: boolean
  className?: string
  selectedFiles: File[]
  onFilesChange: (files: File[]) => void
  extendedThinking: boolean
  onExtendedThinkingChange: (enabled: boolean) => void
  /** SSE ストリーミング中フラグ（true の間だけ停止ボタンを有効化する） */
  isStreaming: boolean
  /** 停止ボタン押下時のハンドラ（AbortController.abort() を呼ぶ） */
  onStop: () => void
}

export function ChatInput({
  input,
  setInput,
  handleSubmit,
  isLoading,
  className = "",
  selectedFiles,
  onFilesChange,
  extendedThinking,
  onExtendedThinkingChange,
  isStreaming,
  onStop,
}: ChatInputProps) {
  const { role } = useRole()
  const isViewer = role === "viewer"
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Auto-resize the textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current
    if (textarea) {
      textarea.style.height = "0px"
      const scrollHeight = textarea.scrollHeight
      textarea.style.height = scrollHeight + "px"
    }
  }, [input])

  // 送信は ⌘/Ctrl+Enter のみ。Enter 単独は改行（IME変換確定のEnterが送信される事故を防ぐ）
  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      if (e.nativeEvent.isComposing) return
      // ストリーミング中（停止直後の abort クリーンアップ完了前を含む）は再送信を抑止。
      // この窓では isLoading が false になり得るため、isStreaming を明示的に見て
      // 旧ストリームの cleanup と新 startStream のレースを防ぐ。
      if (isStreaming) return
      if (input.trim() || selectedFiles.length > 0) {
        e.preventDefault()
        handleSubmit(e as unknown as FormEvent)
      }
    }
  }

  const handleFileSelect = () => {
    fileInputRef.current?.click()
  }

  // クリップボード貼り付けで添付（⌘/Ctrl+V・claude.ai と同じ体験・2026-07-06）。
  // スクリーンショット等は clipboardData.items に kind="file" で入る。ファイルを
  // 取り込んだ時だけ preventDefault し（ファイルパス等のテキスト混入を防ぐ）、
  // テキストだけの貼り付けは既定動作のまま。検証・合流はダイアログ経路と同じ
  // （isAllowedFile → onFilesChange）。
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (isLoading || isViewer) return
    const pasted = Array.from(e.clipboardData?.items || [])
      .filter((it) => it.kind === "file")
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null)
    if (pasted.length === 0) return
    // スクリーンショットは名前が "image.png" 固定で並ぶため、一意な名前を付け直す
    const named = pasted.map((f, i) => {
      if (f.name && f.name !== "image.png") return f
      const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg")
      return new File([f], `clipboard-${Date.now()}-${i + 1}.${ext}`, {
        type: f.type,
      })
    })
    const valid = named.filter(isAllowedFile)
    if (valid.length === 0) return
    e.preventDefault()
    onFilesChange([...selectedFiles, ...valid])
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const validFiles = files.filter(isAllowedFile)
    if (validFiles.length > 0) {
      onFilesChange([...selectedFiles, ...validFiles])
    }
    e.target.value = ""
  }

  const handleRemoveFile = (index: number) => {
    onFilesChange(selectedFiles.filter((_, i) => i !== index))
  }

  const canSubmit = (input.trim() || selectedFiles.length > 0) && !isLoading && !isViewer

  return (
    <div className={`p-4 w-full ${className}`}>
      <div className="bg-white rounded-lg shadow-lg border border-gray-200">
        <FilePreview files={selectedFiles} onRemove={handleRemoveFile} />
        <form
          onSubmit={handleSubmit}
          className="flex space-x-2 w-full items-end p-3"
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={getAcceptString()}
            onChange={handleFileChange}
            className="hidden"
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleFileSelect}
                disabled={isLoading || isViewer}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                <Paperclip size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">ファイルを添付</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => onExtendedThinkingChange(!extendedThinking)}
                disabled={isLoading || isViewer}
                className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${
                  extendedThinking
                    ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                }`}
              >
                <Brain size={18} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              {extendedThinking ? "思考拡張：ON" : "思考拡張：OFF"}
            </TooltipContent>
          </Tooltip>

          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={isViewer ? "閲覧専用モード" : "メッセージを入力...（⌘/Ctrl+Enterで送信）"}
            disabled={isLoading || isViewer}
            className="flex-1 min-h-[40px] max-h-[200px] resize-none py-2"
            rows={1}
            autoFocus
          />

          {isStreaming ? (
            // ストリーミング中は「停止」ボタン。disabled なし＝考え中でも押せる。
            // type="button" で form submit を起こさず、onStop で AbortController.abort()。
            <Button
              type="button"
              onClick={onStop}
              className="h-10"
            >
              <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
              停止
            </Button>
          ) : (
            <Button type="submit" disabled={!canSubmit} className="h-10">
              {isLoading ? (
                <>
                  <Loader2Icon className="mr-2 h-4 w-4 animate-spin" />
                  考え中...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  送信
                </>
              )}
            </Button>
          )}
        </form>
      </div>
    </div>
  )
}
